import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { NumericFormat } from 'react-number-format';

import { CustomFormLabel } from 'components';
import { Box, FormControl, FormHelperText, OutlinedInput } from '@mui/material';

const NumberFormatCustom = props => {
    const { inputRef, onChange, decimalScale = 2, fixedDecimalScale, ...other } = props;

    return (
        <NumericFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={values => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value,
                    },
                });
            }}
            thousandSeparator={' '}
            decimalSeparator={','}
            valueIsNumericString
            decimalScale={decimalScale}
            fixedDecimalScale={fixedDecimalScale}
            allowNegative={!!other.allowNegative}
        />
    );
};

NumberFormatCustom.propTypes = {
    inputRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    onChange: PropTypes.func,
    name: PropTypes.string,
    decimalScale: PropTypes.number,
    fixedDecimalScale: PropTypes.number,
    allowNegative: PropTypes.bool,
};

const NumberInputComponent = React.forwardRef((props, ref) => (
    <NumberFormatCustom inputRef={ref} {...props} />
));

const CustomTextField = props => {
    const {
        error,
        label,
        showError = true,
        inputStyle,
        formatNumber,
        allowNegative,
        decimalScale,
        fixedDecimalScale,
        formControlProps,
        type,
        fullWidth,
        infoButton,
        ...rest
    } = props;

    const handleNumberKeyDown = useCallback(
        e => {
            if (
                ((e.which <= 90 && e.which >= 65) || e.key === '+' || e.key === '-') &&
                !e.ctrlKey &&
                (e.key === '+' || e.key === '-') &&
                (e.target.value.includes('-') || e.target.value.includes('+') || !allowNegative)
            ) {
                e.preventDefault();
            }
        },
        [allowNegative]
    );

    const handleWheel = useCallback(
        e => {
            if (type === 'number' || formatNumber) {
                e.target.blur();
            }
        },
        [type, formatNumber]
    );

    return (
        <FormControl
            classes={{ root: inputStyle }}
            fullWidth={fullWidth}
            error={!!error}
            {...formControlProps}
        >
            {label && <CustomFormLabel>{label}</CustomFormLabel>}
            {formatNumber ? (
                <Box display="flex" alignItems="center">
                    <OutlinedInput
                        fullWidth
                        inputComponent={NumberInputComponent}
                        inputProps={{ allowNegative, decimalScale, fixedDecimalScale }}
                        type={type}
                        onWheel={handleWheel}
                        {...rest}
                    />
                    {!!infoButton && infoButton}
                </Box>
            ) : (
                <Box display="flex" alignItems="center">
                    <OutlinedInput
                        fullWidth
                        type={type}
                        onKeyDown={type === 'number' ? handleNumberKeyDown : undefined}
                        onWheel={type === 'number' ? handleWheel : undefined}
                        {...rest}
                    />
                    {!!infoButton && infoButton}
                </Box>
            )}
            {!!error && showError && <FormHelperText error={!!error}>{error}</FormHelperText>}
        </FormControl>
    );
};

CustomTextField.propTypes = {
    showError: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    formatNumber: PropTypes.bool,
    allowNegative: PropTypes.bool,
    fullWidth: PropTypes.bool,
    inputStyle: PropTypes.string,
    label: PropTypes.string,
    type: PropTypes.string,
    formControlProps: PropTypes.object,
    infoButton: PropTypes.node,
    decimalScale: PropTypes.number,
    fixedDecimalScale: PropTypes.number,
};

export default CustomTextField;
